import type { GetState } from 'types/redux'
import { mapResponseToReducerObjects } from '@disco/state-management/actions/PlaylistTrackActions'
import { prepareTempItems } from '@disco/utils/upload'
import { verifyFiles } from '@disco/utils/upload'
import {
  TRACKS_UPLOADER_RESET,
  TRACKS_UPLOADER_SAVE_SUCCESS,
  TRACKS_UPLOADER_SET,
  TRACKS_UPLOADER_UPLOAD_SUCCESS,
} from '@disco/constants/ActionTypes'
import { pushUINotification } from '@disco/utils/notifications'
import { finalizeUpload, uploadFileToS3 } from '@disco/utils/upload'
/**
 * Resets the track uploader state to its initial values.
 * @returns Action object to reset track uploads
 */
export function resetTrackUploads(): {
  type: string
} {
  return {
    type: TRACKS_UPLOADER_RESET,
  }
}
/**
 * Uploads a track file to S3 with progress tracking and error handling.
 * @param file - File object to upload
 * @param trackPolicyUrl - S3 upload policy URL for the track
 * @returns Thunk that uploads the file and returns an AbortController
 */
export function uploadTrackFileToS3(
  file: File,
  trackPolicyUrl: string | null | undefined,
): (dispatch: any, getState: GetState) => AbortController {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const { csrfToken } = getState().user
    const abortController = new AbortController()
    // Prepare temp items and verify files
    const [tempItem] = verifyFiles(prepareTempItems([file]), pushUINotification)

    if (!tempItem) return abortController

    // simplify
    dispatch({
      type: TRACKS_UPLOADER_SET,
      track: tempItem,
    })
    uploadFileToS3({
      file: tempItem.file,
      csrfToken,
      uniqueId: tempItem.tempId,
      policyUrl: trackPolicyUrl,
      events: {
        onInfoParse(info) {
          dispatch({
            type: TRACKS_UPLOADER_SET,
            track: info,
          })
        },

        onDuplicateFound(track) {
          const normalizedTrack = mapResponseToReducerObjects(
            dispatch,
            getState,
            track,
          )
          dispatch({
            type: TRACKS_UPLOADER_SET,
            track: normalizedTrack,
          })
        },

        onUploadProgress(track) {
          dispatch({
            type: TRACKS_UPLOADER_SET,
            track,
          })
        },

        onUploadError() {
          dispatch({
            type: TRACKS_UPLOADER_RESET,
          })
        },
      },
      abortSignal: abortController.signal,
    }).then(({ needsFinalizeUpload, uploadPolicy }) => {
      dispatch({
        type: TRACKS_UPLOADER_UPLOAD_SUCCESS,
        filename: file.name,
        needsFinalizeUpload,
        uploadPolicy,
        filesize: file.size,
      })
    })
    return abortController
  }
}
/**
 * Finalizes a track upload after S3 upload completion.
 * @param trackPolicyUrl - S3 upload policy URL for the track
 * @param transcode - Whether to transcode the uploaded track
 * @returns Thunk that finalizes the upload and creates the track record
 */
export function finalizeTrackUpload(
  trackPolicyUrl: string | null | undefined,
  transcode: boolean,
): (dispatch: any, getState: GetState) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const { csrfToken } = getState().user
    const { filename, uploadPolicy, filesize } = getState().trackUploader
    if (!filename || !filesize) return
    const track = await finalizeUpload({
      filename,
      uploadPolicy,
      csrfToken,
      policyUrl: trackPolicyUrl,
      transcode,
      filesize,
    })
    const normalizedTrack = mapResponseToReducerObjects(
      dispatch,
      getState,
      track,
    )
    dispatch({
      type: TRACKS_UPLOADER_SAVE_SUCCESS,
      track: normalizedTrack,
    })
  }
}
